/**
 * Coalescing store for in-app missed-call toasts.
 *
 * Contract (see task-151):
 *  - Keyed by `callerId`. Repeat missed calls from the same caller within
 *    `coalesceWindowMs` (default 60s) mutate the existing entry's count +
 *    timestamp instead of pushing a new toast.
 *  - At most `maxToasts` (default 3) entries are visible. Pushing a NEW
 *    caller while at capacity evicts the oldest entry (lowest `updatedAt`).
 *  - The store is a pure observable: callers subscribe to receive snapshots.
 *    All UI / audio / focus behaviour lives in the consumer; the store
 *    itself never plays sound and never steals focus.
 *  - The store has no implicit auto-dismiss timer — entries persist until
 *    `dismiss(callerId)` or `clear()` is called by the UI. (The UI layer
 *    owns the visible lifetime so it can pause on hover, etc.)
 */

export interface MissedCallToastEntry {
  callerId: number;
  callerName: string;
  modality: 'audio' | 'video';
  channelId: string;
  count: number;
  /** Wall-clock ms of the most recent missed call from this caller. */
  updatedAt: number;
}

export interface MissedCallToastsConfig {
  coalesceWindowMs?: number;
  maxToasts?: number;
  /** Injectable clock for deterministic tests. Defaults to `Date.now`. */
  now?: () => number;
}

export interface MissedCallToastsStore {
  push(input: Omit<MissedCallToastEntry, 'count' | 'updatedAt'>): void;
  dismiss(callerId: number): void;
  clear(): void;
  getSnapshot(): MissedCallToastEntry[];
  subscribe(listener: (entries: MissedCallToastEntry[]) => void): () => void;
}

export const DEFAULT_COALESCE_WINDOW_MS = 60_000;
export const DEFAULT_MAX_TOASTS = 3;

export function createMissedCallToastsStore(
  config: MissedCallToastsConfig = {},
): MissedCallToastsStore {
  const coalesceWindowMs = config.coalesceWindowMs ?? DEFAULT_COALESCE_WINDOW_MS;
  const maxToasts = config.maxToasts ?? DEFAULT_MAX_TOASTS;
  const now = config.now ?? Date.now;

  // Insertion-ordered map; on coalesce we re-insert so "most recent" is last.
  const entries = new Map<number, MissedCallToastEntry>();
  const listeners = new Set<(entries: MissedCallToastEntry[]) => void>();

  function snapshot(): MissedCallToastEntry[] {
    return Array.from(entries.values());
  }

  function emit(): void {
    const snap = snapshot();
    listeners.forEach(l => {
      try {
        l(snap);
      } catch {
        // Listener errors must not break the store.
      }
    });
  }

  return {
    push(input) {
      const ts = now();
      const existing = entries.get(input.callerId);
      if (existing && ts - existing.updatedAt <= coalesceWindowMs) {
        // Coalesce: bump count + refresh timestamp + reorder to "most recent".
        const merged: MissedCallToastEntry = {
          ...existing,
          callerName: input.callerName || existing.callerName,
          modality: input.modality,
          channelId: input.channelId,
          count: existing.count + 1,
          updatedAt: ts,
        };
        entries.delete(input.callerId);
        entries.set(input.callerId, merged);
        emit();
        return;
      }

      // New entry (or stale existing one — treat the stale one as a fresh
      // toast: drop it first so we don't double-count past the window).
      if (existing) entries.delete(input.callerId);

      // Cap enforcement: evict oldest by insertion order.
      while (entries.size >= maxToasts) {
        const oldestKey = entries.keys().next().value as number | undefined;
        if (oldestKey === undefined) break;
        entries.delete(oldestKey);
      }

      entries.set(input.callerId, {
        callerId: input.callerId,
        callerName: input.callerName,
        modality: input.modality,
        channelId: input.channelId,
        count: 1,
        updatedAt: ts,
      });
      emit();
    },

    dismiss(callerId) {
      if (entries.delete(callerId)) emit();
    },

    clear() {
      if (entries.size === 0) return;
      entries.clear();
      emit();
    },

    getSnapshot: snapshot,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Process-wide singleton used by the WebSocket dispatcher and the
 * `<MissedCallToasts />` UI. Tests should construct a fresh store via
 * `createMissedCallToastsStore` rather than touching this instance.
 */
export const missedCallToastsStore = createMissedCallToastsStore();
